import { useState, useRef } from 'react'
import { RiMusic2Line, RiAddLine, RiLoader4Line } from '@remixicon/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { api } from '@/lib/api'

export function SongSearch({
	playlistId,
	songCount,
	accentColor,
}: {
	playlistId: string
	songCount: number
	accentColor: string
}) {
	const [open, setOpen] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [debouncedQuery, setDebouncedQuery] = useState('')
	const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
	const queryClient = useQueryClient()

	const searchQuery = useQuery({
		queryKey: ['songSearch', debouncedQuery],
		queryFn: async () => {
			const response = await api.api.songs.search.get({
				query: { q: debouncedQuery, offset: 0 },
			})
			if (response.error) throw new Error('Search failed')
			return response.data
		},
		enabled: debouncedQuery.length >= 2,
	})

	const addMutation = useMutation({
		mutationFn: async (track: { title: string; artist: string; spotifyUri: string }) => {
			const response = await api.api.songs.post({
				name: track.title,
				artist: track.artist,
				spotifyUri: track.spotifyUri,
				playlistId,
				index: songCount,
			})
			if (response.error) throw new Error('Failed to create song')
			return response.data
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['songs', { playlistId }] })
			setOpen(false)
			setInputValue('')
			setDebouncedQuery('')
		},
	})

	function handleSearchChange(value: string) {
		setInputValue(value)
		clearTimeout(timerRef.current)
		timerRef.current = setTimeout(() => setDebouncedQuery(value), 300)
	}

	const tracks = searchQuery.data?.items ?? []

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="mb-3 w-full justify-start text-muted-foreground"
				>
					<RiAddLine className="size-3.5" />
					Search songs to add...
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-(--radix-popover-trigger-width) ring-0 p-0" align="start">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Search songs..."
						value={inputValue}
						onValueChange={handleSearchChange}
					/>
					<CommandList>
						{searchQuery.isLoading && (
							<div className="flex items-center justify-center py-6">
								<RiLoader4Line className="size-4 animate-spin text-muted-foreground" />
							</div>
						)}
						{debouncedQuery.length >= 2 && !searchQuery.isLoading && tracks.length === 0 && (
							<CommandEmpty>No songs found.</CommandEmpty>
						)}
						{tracks.length > 0 && (
							<CommandGroup>
								{tracks.map((track) => (
									<CommandItem
										key={track.id}
										value={String(track.id)}
										onSelect={() =>
											addMutation.mutate({
												title: track.title,
												artist: track.artist,
												spotifyUri: String(track.id),
											})
										}
										disabled={addMutation.isPending}
									>
										<div
											className="flex size-8 shrink-0 items-center justify-center rounded"
											style={{
												backgroundColor: `color-mix(in oklch, ${accentColor} 8%, transparent)`,
											}}
										>
											<RiMusic2Line className="size-3" style={{ color: accentColor }} />
										</div>
										<div className="min-w-0 flex-1">
											<p className="truncate text-[13px] font-medium leading-tight">
												{track.title}
											</p>
											<p className="truncate text-[11px] text-muted-foreground/60">
												{track.artist}
												{track.album.title && ` · ${track.album.title}`}
											</p>
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
